/*
  assets/carnes-kar-testimonios.js
  Carrusel de testimonios (sections/carnes-kar-testimonios.liquid).
  Extiende SliderComponent de Dawn (assets/global.js) — mismo patrón que
  SlideshowComponent — agregando:
    - autoplay configurable por schema (data-autoplay / data-speed)
    - pausa en hover (mouseenter/mouseleave) y en focus dentro (focusin/focusout)
    - wrap: al pasar de la última página vuelve a la primera (y viceversa)
    - dots activos + modo grid estático cuando todas las cards caben en el viewport
    - respeta prefers-reduced-motion (sin autoplay; el scroll-behavior lo maneja
      component-slider.css)
  Cargado con defer DESPUÉS de global.js, por lo que SliderComponent ya existe.
*/

if (!customElements.get('ck-testimonios-carousel')) {
  class CkTestimoniosCarousel extends SliderComponent {
    constructor() {
      super();
      if (!this.slider || !this.nextButton) return;

      this.enableSliderLooping = true; // el super no deshabilita las flechas (hay wrap)
      this.prevButton.removeAttribute('disabled');
      this.nextButton.removeAttribute('disabled');

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.isHovered = false;
      this.isFocused = false;

      this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollToPage(index);
        });
      });

      // Pausa del timer en hover y en focus por teclado; reanuda al salir.
      this.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.pause();
      });
      this.addEventListener('mouseleave', () => {
        this.isHovered = false;
        this.maybePlay();
      });
      this.addEventListener('focusin', () => {
        this.isFocused = true;
        this.pause();
      });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.isFocused = false;
        this.maybePlay();
      });
      this.reducedMotion.addEventListener('change', () => {
        this.reducedMotion.matches ? this.pause() : this.maybePlay();
      });

      // Re-evaluar con los listeners y dots ya montados (el super corrió antes).
      this.initPages();
    }

    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) {
        this.setStaticMode(true);
        return;
      }
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      // Math.round (no floor como el super): con gap entre cards, floor subestima
      // las cards visibles (p. ej. 3 de 359px + gaps 26px → 2.93 debe ser 3).
      this.slidesPerPage = Math.max(1, Math.round(this.slider.clientWidth / this.sliderItemOffset));
      this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);

      // Si todo cabe en el viewport no hay nada que rotar → grid estático.
      const allVisible = this.slider.scrollWidth <= this.slider.clientWidth + 1;
      this.setStaticMode(allVisible || this.totalPages < 2);

      // Dots: solo tantos como páginas alcanzables en este viewport.
      if (this.dots) {
        this.dots.forEach((dot, index) => {
          dot.hidden = index >= this.totalPages;
        });
      }

      this.update();
    }

    setStaticMode(isStatic) {
      this.isStatic = isStatic;
      this.classList.toggle('ck-testimonios__carrusel--estatico', isStatic);
      isStatic ? this.pause() : this.maybePlay();
    }

    update() {
      super.update();
      if (!this.dots || !this.dots.length) return;
      const activeIndex = Math.min((this.currentPage || 1) - 1, this.dots.length - 1);
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('slider-counter__link--active', index === activeIndex);
        if (index === activeIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    onButtonClick(event) {
      event.preventDefault();
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      if (event.currentTarget.name === 'next' && this.currentPage >= this.totalPages) {
        this.slider.scrollTo({ left: 0 }); // wrap → vuelve al inicio
      } else if (event.currentTarget.name === 'previous' && this.currentPage <= 1) {
        this.slider.scrollTo({ left: maxScroll }); // wrap → va al final
      } else {
        super.onButtonClick(event);
      }
    }

    scrollToPage(index) {
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      this.slider.scrollTo({ left: Math.min(index * this.sliderItemOffset, maxScroll) });
    }

    maybePlay() {
      if (
        !this.autoplayEnabled ||
        this.isStatic ||
        this.isHovered ||
        this.isFocused ||
        (this.reducedMotion && this.reducedMotion.matches)
      )
        return;
      this.play();
    }

    play() {
      this.slider.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(this.autoRotate.bind(this), this.autoplaySpeed);
    }

    pause() {
      this.slider.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
    }

    autoRotate() {
      const position = this.currentPage >= this.totalPages ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
      this.slider.scrollTo({ left: position });
    }
  }

  customElements.define('ck-testimonios-carousel', CkTestimoniosCarousel);
}
